// drbooking Visitor Tracking v8.0
// Tracks all page visits with geolocation

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get visitor's location using free IP geolocation API
const getVisitorLocation = async () => {
  try {
    // Using ipapi.co free tier (1000 requests/day)
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    return {
      country_code: data.country_code,
      country_name: data.country_name,
      city: data.city,
      region: data.region,
      latitude: data.latitude,
      longitude: data.longitude,
      timezone: data.timezone,
      isp: data.org
    };
  } catch (error) {
    console.log('Geolocation unavailable');
    return null;
  }
};

// Get session ID (create one if doesn't exist)
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('drbooking_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('drbooking_session_id', sessionId);
    sessionStorage.setItem('drbooking_session_start', Date.now().toString());
    sessionStorage.setItem('drbooking_page_count', '0');
  }
  return sessionId;
};

// Increment page count
const incrementPageCount = () => {
  const count = parseInt(sessionStorage.getItem('drbooking_page_count') || '0') + 1;
  sessionStorage.setItem('drbooking_page_count', count.toString());
  return count;
};

// Get referrer
const getReferrer = () => {
  const ref = document.referrer;
  if (!ref) return 'direct';
  if (ref.includes('google')) return 'google';
  if (ref.includes('facebook')) return 'facebook';
  if (ref.includes('instagram')) return 'instagram';
  if (ref.includes('tiktok')) return 'tiktok';
  if (ref.includes('twitter') || ref.includes('x.com')) return 'twitter';
  if (ref.includes('linkedin')) return 'linkedin';
  if (ref.includes('youtube')) return 'youtube';
  return 'other';
};

// Get device info
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  const isMobile = /Mobile|Android|iPhone|iPad/.test(ua);
  const isTablet = /iPad|Tablet/.test(ua);
  
  let browser = 'other';
  if (ua.includes('Chrome')) browser = 'chrome';
  else if (ua.includes('Firefox')) browser = 'firefox';
  else if (ua.includes('Safari')) browser = 'safari';
  else if (ua.includes('Edge')) browser = 'edge';
  
  return {
    device_type: isTablet ? 'tablet' : (isMobile ? 'mobile' : 'desktop'),
    browser,
    screen_width: window.innerWidth,
    screen_height: window.innerHeight,
    language: navigator.language
  };
};

// Track page visit
export const trackPageVisit = async (pagePath, pageTitle = '') => {
  try {
    const sessionId = getSessionId();
    const pageCount = incrementPageCount();
    const location = await getVisitorLocation();
    const device = getDeviceInfo();
    const sessionStart = parseInt(sessionStorage.getItem('drbooking_session_start') || Date.now().toString());
    const timeOnSite = Math.round((Date.now() - sessionStart) / 1000); // seconds

    const trackingData = {
      session_id: sessionId,
      page: pagePath,
      page_title: pageTitle,
      page_count: pageCount,
      time_on_site_seconds: timeOnSite,
      referrer: getReferrer(),
      ...location,
      ...device
    };

    await axios.post(`${API_URL}/api/analytics/track`, trackingData);
  } catch (error) {
    // Silent fail - don't disrupt user experience
    console.log('Tracking error (silent)');
  }
};

// React Hook for automatic tracking
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page on route change
    const pageTitle = document.title || location.pathname;
    trackPageVisit(location.pathname, pageTitle);
  }, [location.pathname]);
};

// Component wrapper for tracking
const VisitorTracker = ({ children }) => {
  usePageTracking();
  return children;
};

export default VisitorTracker;
